import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import { FaPlus, FaDownload } from "react-icons/fa6";
import AddEditRfid from "./AddEditRfid";
import { getAllZones, getWardsByZoneId, getAllRfidMaster } from "../../Apis/rfidApi";
import { ExportRfid } from "./ExportRfid";
import RfidExpandableTable from "./RfidExpandableTable";
import { RfidMapContent } from "./RfidMapView";
import {
  useRfidStore,
  DEFAULT_RFID_ZONE_ID,
  DEFAULT_RFID_ZONE_NAME,
} from "./store/useRfidStore";
import useGoogleMapsLoader from "../../../Hooks/useGoogleMapsLoader";
import {
  downloadQRCodeImage,
  generateQRCodeSVG,
  handleDownloadQRCode,
  getRfidQrKey,
  mapRfidToQrData,
  getRfidQrLabelRows,
} from "./utils/rfidQrUtils";
import ModalComponent from "../../Common/popup_modal/ModalComponent";
import RfidBulkUpload from "./rfid_bulk_upload/RfidBulkUpload";
import { useRfidBulkUploadStore } from "./rfid_bulk_upload/useRfidBulkUploadStore";

const Rfid = () => {
  const [showModal, setShowModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [viewMapRow, setViewMapRow] = useState(null);
  const [zones, setZones] = useState([]);
  const [wards, setWards] = useState([]);
  const [resolvedDefaultZoneId, setResolvedDefaultZoneId] = useState(
    DEFAULT_RFID_ZONE_ID
  );

  const [qrCodeModal, setQrCodeModal] = useState(false);
  const [qrRfidData, setQrRfidData] = useState(null);
  const [selectedQRs, setSelectedQRs] = useState([]);
  const [isExportQRDownloadLoading, setIsExportQRDownloadLoading] =
    useState(false);
  const [, setDownloadProgress] = useState(0);
  const [downloadStatus, setDownloadStatus] = useState("");
  const {
    showBulkUploadModal,
    bulkUploadStep,
    openBulkUploadModal,
    closeBulkUploadModal,
  } = useRfidBulkUploadStore();

  useGoogleMapsLoader();

  const {
    filteredRfidDevices,
    rfidCategories,
    isLoading,
    filters,
    fetchRfidDevices,
    fetchRfidCategories,
    updateFilter,
    resetFilters,
  } = useRfidStore();

  const getZoneData = async () => {
    let zoneId = filters.zoneId || DEFAULT_RFID_ZONE_ID;
    try {
      const zoneResponse = await getAllZones({ isActive: true });
      const activeZones = zoneResponse.filter((zone) => zone.isActive);
      setZones(activeZones);

      const defaultZone =
        activeZones.find((zone) => zone.zoneName === DEFAULT_RFID_ZONE_NAME) ||
        activeZones.find(
          (zone) => String(zone.zoneId) === DEFAULT_RFID_ZONE_ID
        );
      zoneId = String(defaultZone?.zoneId ?? DEFAULT_RFID_ZONE_ID);
      setResolvedDefaultZoneId(zoneId);
      updateFilter("zoneId", zoneId);

      await loadWards(zoneId);
      return zoneId;
    } catch (error) {
      console.error("Error fetching zones:", error);
      await loadWards(zoneId);
      return zoneId;
    }
  };

  const loadWards = async (zoneId) => {
    if (!zoneId) {
      setWards([]);
      return;
    }
    try {
      const response = await getWardsByZoneId(zoneId);
      setWards(response.filter((circle) => circle.isActive));
    } catch (error) {
      console.error("Error fetching wards:", error);
      setWards([]);
    }
  };

  useEffect(() => {
    const init = async () => {
      const zoneId = await getZoneData();
      fetchRfidCategories();
      fetchRfidDevices({ zoneId, pageNumber: 1 });
    };

    init();
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    updateFilter(name, value);
    if (name === "zoneId") {
      updateFilter("circleId", "");
      loadWards(value);
    }
  };

  const handleApplyFilters = () => {
    fetchRfidDevices({ pageNumber: 1 });
  };

  const handleResetFilters = () => {
    setResolvedDefaultZoneId(DEFAULT_RFID_ZONE_ID);
    resetFilters();
    loadWards(DEFAULT_RFID_ZONE_ID);
  };

  const handleAdd = () => {
    setIsEditMode(false);
    setEditRow(null);
    setShowModal(true);
  };

  const handleEdit = (row) => {
    setIsEditMode(true);
    setEditRow(row);
    setShowModal(true);
  };

  const handleViewOnMap = (row) => {
    if (!row) return;
    const lat = Number(row?.latitude ?? row?.Latitude);
    const lng = Number(row?.longitude ?? row?.Longitude);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;
    setViewMapRow(row);
  };

  const closeViewMapModal = () => setViewMapRow(null);

  const handleSelectQR = (qrKey) => {
    if (!qrKey) return;
    setSelectedQRs((prev) =>
      prev.includes(qrKey) ? prev.filter((id) => id !== qrKey) : [...prev, qrKey]
    );
  };

  const handleViewQR = (row) => {
    setQrRfidData(mapRfidToQrData(row));
    setQrCodeModal(true);
  };

  const handleBulkUpload = () => {
    openBulkUploadModal();
  };

  const handleDownloadSelectedQRCodes = async () => {
    try {
      setIsExportQRDownloadLoading(true);
      setDownloadProgress(0);
      setDownloadStatus("Preparing download...");

      let rowsToDownload;
      if (selectedQRs.length > 0) {
        rowsToDownload = filteredRfidDevices.filter((row) =>
          selectedQRs.includes(getRfidQrKey(row))
        );
      } else {
        const response = await getAllRfidMaster({
          zoneId: filters.zoneId || undefined,
          circleId: filters.circleId || undefined,
          assetCategoryId: filters.assetCategoryId || undefined,
          pageNumber: 1,
          pageSize: 5000,
        });
        const list = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response?.items)
              ? response.items
              : Array.isArray(response?.rfidMasters)
                ? response.rfidMasters
                : [];
        rowsToDownload = list;
      }

      const itemsToDownload = rowsToDownload
        .map(mapRfidToQrData)
        .filter((item) => item.qrContent);

      if (itemsToDownload.length > 0) {
        setDownloadStatus(`Processing ${itemsToDownload.length} items...`);

        await handleDownloadQRCode(
          itemsToDownload,
          (progress, processed, total) => {
            setDownloadProgress(progress);
            setDownloadStatus(
              `Processing ${processed}/${total} items (${progress}%)`
            );
          }
        );

        setDownloadStatus("Download completed!");
      } else {
        console.warn("No RFID data available for QR export.");
        setDownloadStatus("No items to download");
      }
    } catch (error) {
      console.error("Error fetching RFID data for QR export: ", error);
      setDownloadStatus("Download failed");
    } finally {
      setIsExportQRDownloadLoading(false);
      setTimeout(() => {
        setDownloadProgress(0);
        setDownloadStatus("");
      }, 2000);
    }
  };

  return (
    <>
      <div className="content text-start">
        <div className="page-content">
          <div className="d-flex flex-column flex-md-row align-items-start justify-content-md-between gap-2 mt-3">
            <div className="flex-grow-1 min-w-0">
              <h5 className="mb-1 fw-semibold">RFID Information</h5>
             
            </div>
            <div
              className="d-flex align-items-center flex-wrap ms-md-auto"
              style={{ position: "relative", gap: "6px" }}
            >
              <button
                type="button"
                className="button-container ms-0"
                style={{ marginLeft: 0 }}
                onClick={handleAdd}
              >
                <FaPlus className="font-size-20 me-1" /> Add RFID
              </button>
             
              <button
                type="button"
                className="btn btn-excel ms-0"
                style={{ marginLeft: 0 }}
                onClick={handleBulkUpload}
              >
                <i className="fa-solid fa-upload me-1 icon-container"></i>
                Bulk Upload
              </button>
               <ExportRfid
                rfidData={filteredRfidDevices}
                extraItems={[
                  {
                    label: "Download QR",
                    icon: "fa-solid fa-qrcode",
                    onClick: () => handleDownloadSelectedQRCodes(),
                    loading: isExportQRDownloadLoading,
                  },
                ]}
              />
              {isExportQRDownloadLoading && downloadStatus && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    right: 0,
                    marginTop: 4,
                    fontSize: "12px",
                    color: "#666",
                    whiteSpace: "nowrap",
                    pointerEvents: "none",
                  }}
                >
                  {downloadStatus}
                </div>
              )}
            </div>
          </div>

          <div className="card shadow mt-3">
            <div className="card-body">
              <div className="row align-items-end">
                <div className="col-md-3 mb-3">
                  <label className="form-label">Zone</label>
                  <select
                    name="zoneId"
                    value={
                      filters.zoneId ||
                      resolvedDefaultZoneId ||
                      DEFAULT_RFID_ZONE_ID
                    }
                    onChange={handleFilterChange}
                    className="form-select bg-white"
                  >
                    {zones.length > 0 ? (
                      zones.map((zone) => (
                        <option key={zone.zoneId} value={String(zone.zoneId)}>
                          {zone.zoneName}
                        </option>
                      ))
                    ) : (
                      <option value={DEFAULT_RFID_ZONE_ID}>
                        {DEFAULT_RFID_ZONE_NAME}
                      </option>
                    )}
                  </select>
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Ward</label>
                  <Select
                    inputId="circleId"
                    name="circleId"
                    classNamePrefix="react-select"
                    options={wards.map((ward) => ({
                      value: String(ward.circleId),
                      label: ward.circleName,
                    }))}
                    value={
                      wards
                        .map((ward) => ({
                          value: String(ward.circleId),
                          label: ward.circleName,
                        }))
                        .find(
                          (option) => option.value === String(filters.circleId)
                        ) || null
                    }
                    onChange={(selected) =>
                      updateFilter("circleId", selected ? selected.value : "")
                    }
                    placeholder="Select Wards"
                    isClearable
                    menuPlacement="auto"
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  />
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Asset Category</label>
                  <Select
                    inputId="assetCategoryId"
                    name="assetCategoryId"
                    classNamePrefix="react-select"
                    options={rfidCategories.map((category) => ({
                      value: String(category.assetCategoryId),
                      label: category.assetCategoryName,
                    }))}
                    value={
                      rfidCategories
                        .map((category) => ({
                          value: String(category.assetCategoryId),
                          label: category.assetCategoryName,
                        }))
                        .find(
                          (option) =>
                            option.value === String(filters.assetCategoryId)
                        ) || null
                    }
                    onChange={(selected) =>
                      updateFilter(
                        "assetCategoryId",
                        selected ? selected.value : ""
                      )
                    }
                    placeholder="Select Asset Category"
                    isClearable
                    menuPlacement="auto"
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  />
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label" aria-hidden="true" style={{ visibility: "hidden" }}>
                    &nbsp;
                  </label>
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      style={{
                        backgroundColor: "var(--btn-primary-bg)",
                        color: "var(--btn-primary-text)",
                        border: "none",
                        fontWeight: 600,
                        padding: "6px 16px",
                        borderRadius: "6px",
                        fontSize: "13px",
                        whiteSpace: "nowrap",
                      }}
                      onClick={handleApplyFilters}
                    >
                      APPLY FILTERS
                    </button>
                    <button
                      type="button"
                      style={{
                        backgroundColor: "#FDE8E8",
                        color: "#8B1A1A",
                        border: "none",
                        fontWeight: 600,
                        padding: "6px 16px",
                        borderRadius: "6px",
                        fontSize: "13px",
                        whiteSpace: "nowrap",
                      }}
                      onClick={handleResetFilters}
                    >
                      RESET
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <RfidExpandableTable
                  loading={isLoading}
                  rows={filteredRfidDevices}
                  onEdit={handleEdit}
                  onViewMap={handleViewOnMap}
                  selectedQRs={selectedQRs}
                  onSelectQR={handleSelectQR}
                  onViewQR={handleViewQR}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <AddEditRfid
        showModal={showModal}
        setShowModal={setShowModal}
        editRow={editRow}
        isEditMode={isEditMode}
        zoneData={zones}
        defaultZoneId={resolvedDefaultZoneId}
      />

      <ModalComponent
        title={
          bulkUploadStep === "form"
            ? "RFID - Bulk Upload"
            : "Check Uploaded File"
        }
        show={showBulkUploadModal}
        onHide={closeBulkUploadModal}
        size={bulkUploadStep === "form" ? "lg" : "xl"}
        handleClose={closeBulkUploadModal}
      >
        <RfidBulkUpload onSaved={() => fetchRfidDevices({ pageNumber: 1 })} />
      </ModalComponent>

      <Modal
        show={!!viewMapRow}
        onHide={closeViewMapModal}
        size="xl"
        centered
        aria-labelledby="rfid-view-map-title"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title id="rfid-view-map-title">View on Map</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 16 }}>
          {viewMapRow && (
            <RfidMapContent
              key={viewMapRow.assetRfidId ?? viewMapRow.rfidCode}
              row={viewMapRow}
            />
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={qrCodeModal}
        onHide={() => {
          setQrCodeModal(false);
          setQrRfidData(null);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>QR Code</Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            display: "flex",
            justifyContent: "center",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {qrRfidData?.qrContent && (
            <div
              id="qrCode"
              style={{ margin: "10px 0" }}
              dangerouslySetInnerHTML={{
                __html: generateQRCodeSVG(qrRfidData.qrContent),
              }}
            />
          )}
          {qrRfidData?.qrContent && (
            <div style={{ textAlign: "center", marginBottom: "10px", width: "100%" }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  maxWidth: "300px",
                  margin: "0 auto",
                }}
              >
                {getRfidQrLabelRows(qrRfidData).map((item, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                      marginBottom: "7px",
                      fontSize: "14px",
                    }}
                  >
                    <span
                      style={{
                        fontWeight: "bold",
                        textAlign: "left",
                        width: "150px",
                      }}
                    >
                      {item.label}:
                    </span>
                    <span style={{ textAlign: "left", flex: 1 }}>
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={() => {
              downloadQRCodeImage(qrRfidData);
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              backgroundColor: "#1A1A1A",
              color: "#FFFFFF",
              border: "none",
              borderRadius: 12,
              padding: "10px 20px",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.18)",
            }}
          >
            <FaDownload size={14} />
            Download QR
          </button>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Rfid;
